import React from 'react'
import { motion } from 'framer-motion'
import { 
  Folder, 
  FileText, 
  Image, 
  Video, 
  FileArchive, 
  File,
  Lock
} from 'lucide-react'

const FileGrid = ({ files, onItemClick, isFolder = false }) => {
  const getFileIcon = (fileName, isFolder) => {
    if (isFolder) return Folder
    
    const extension = fileName.split('.').pop()?.toLowerCase()
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
      return Image
    }
    if (['mp4', 'webm', 'avi', 'mov'].includes(extension)) {
      return Video
    }
    if (['txt', 'md', 'json', 'csv'].includes(extension)) {
      return FileText
    }
    if (['zip', 'rar', '7z', 'tar'].includes(extension)) {
      return FileArchive
    }
    
    return File
  }

  const getFileColor = (fileName, isFolder) => {
    if (isFolder) return 'text-yellow-500 bg-yellow-50'
    
    const extension = fileName.split('.').pop()?.toLowerCase()
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
      return 'text-cyan-500 bg-cyan-50'
    }
    if (['mp4', 'webm', 'avi', 'mov'].includes(extension)) {
      return 'text-purple-500 bg-purple-50'
    }
    if (['txt', 'md', 'json', 'csv'].includes(extension)) {
      return 'text-blue-500 bg-blue-50'
    }
    if (['zip', 'rar', '7z', 'tar'].includes(extension)) {
      return 'text-orange-500 bg-orange-50'
    }
    
    return 'text-gray-500 bg-gray-50'
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4"
    >
      {files.map((file, index) => {
        const IconComponent = getFileIcon(file.displayName, file.isFolder)
        const colorClasses = getFileColor(file.displayName, file.isFolder)
        
        return (
          <motion.div
            key={file.name || index}
            variants={itemVariants}
            whileHover={{ 
              scale: 1.05,
              transition: { type: "spring", stiffness: 300 }
            }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onItemClick(file)}
            className="file-item group"
          >
            {/* File Icon */}
            <div className={`w-16 h-16 rounded-lg ${colorClasses} flex items-center justify-center mb-3 mx-auto relative group-hover:scale-110 transition-transform`}>
              <IconComponent className="w-8 h-8" />
              
              {/* Encryption indicator */}
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-sm">
                <Lock className="w-3 h-3 text-gray-400" />
              </div>
            </div>
            
            {/* File Name */}
            <div className="text-center">
              <p 
                className="font-medium text-sm text-gray-900 truncate mb-1" 
                title={file.displayName}
              >
                {file.displayName}
              </p>
              
              {/* File Info */}
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                {file.isFolder ? (
                  <span className="flex items-center gap-1">
                    <Folder className="w-3 h-3" />
                    Folder
                  </span>
                ) : (
                  <>
                    {file.size && (
                      <span>{formatFileSize(file.size)}</span>
                    )}
                    <Lock className="w-3 h-3" />
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )
      })}
    </motion.div>
  )
}

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return ''
  
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`
}

export default FileGrid
